<template>
    <v-dialog :value="value" persistent max-width="900" @input="$emit('input', $event)">
        <v-card>
            <v-card-title class="headline">
                {{ $t('crudVeiculos.odometerValidity.title') }}
                <span v-if="veiculo" class="ml-2 grey--text text-subtitle-1">{{ veiculo.numero_ordem }}</span>
                <v-tooltip bottom max-width="400">
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="ml-2" color="grey" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                    </template>
                    <span>{{ $t('crudVeiculos.odometerValidity.description') }}</span>
                </v-tooltip>
            </v-card-title>
            <v-card-text>
                <v-form v-if="canEdit" ref="form" v-model="formValido">
                    <v-row dense align="center">
                        <v-col cols="12" sm="6" md="3">
                            <v-datetime-picker v-model="dtInicio" :label="$t('crudVeiculos.odometerValidity.start')"
                                :time-picker-props="timeProps" :text-field-props="{ prependIcon: 'mdi-calendar' }"
                                time-format="HH:mm:ss" date-format="dd/MM/yyyy" :clear-text="$t('datetime.close')">
                                <template slot="dateIcon"><v-icon>mdi-calendar</v-icon></template>
                                <template slot="timeIcon"><v-icon>mdi-clock</v-icon></template>
                            </v-datetime-picker>
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                            <v-datetime-picker v-model="dtFim" :label="$t('crudVeiculos.odometerValidity.end')"
                                :time-picker-props="timeProps"
                                :text-field-props="{ prependIcon: 'mdi-calendar', hint: $t('crudVeiculos.odometerValidity.endHint'), persistentHint: true }"
                                time-format="HH:mm:ss" date-format="dd/MM/yyyy" :clear-text="$t('datetime.close')">
                                <template slot="dateIcon"><v-icon>mdi-calendar</v-icon></template>
                                <template slot="timeIcon"><v-icon>mdi-clock</v-icon></template>
                            </v-datetime-picker>
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field v-model="observacao" :label="$t('crudVeiculos.odometerValidity.notes')"
                                counter="255" maxlength="255"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="2" class="d-flex justify-end">
                            <v-btn v-if="editando" icon small class="mr-1" @click="limpaForm">
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                            <v-btn color="primary" small :loading="salvando" :disabled="!dtInicio" @click="salvar">
                                {{ editando ? $t('button.save') : $t('crudVeiculos.odometerValidity.add') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-form>
                <v-data-table :headers="headers" :items="vigencias" :loading="loading" dense class="mt-2"
                    sort-by="dt_inicio_vigencia" sort-desc :no-data-text="$t('crudVeiculos.odometerValidity.empty')">
                    <template v-slot:item.dt_inicio_vigencia="{ item }">{{ item.dt_inicio_vigencia_extenso }}</template>
                    <template v-slot:item.dt_fim_vigencia="{ item }">
                        {{ item.dt_fim_vigencia_extenso || $t('crudVeiculos.odometerValidity.indefinite') }}
                    </template>
                    <template v-slot:item.status_vigencia="{ item }">
                        <v-chip x-small :color="corStatus[item.status_vigencia]" text-color="white">
                            {{ $t('crudVeiculos.odometerValidity.status.' + item.status_vigencia) }}
                        </v-chip>
                    </template>
                    <template v-slot:item.actions="{ item }">
                        <v-icon small class="mr-2" color="green" :disabled="!canEdit" @click="editar(item)">mdi-pencil</v-icon>
                        <v-icon small color="red" :disabled="!canDelete" @click="excluir(item)">mdi-delete</v-icon>
                    </template>
                </v-data-table>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="$emit('input', false)">{{ $t('button.close') }}</v-btn>
            </v-card-actions>
        </v-card>
        <ConfirmDeleteDialog v-model="confirmaExclusao" :title="$t('system.alert')"
            :message="$t('crudVeiculos.odometerValidity.confirmDelete')" :cancel-text="$t('button.cancel')"
            :confirm-text="$t('button.delete')" @confirm="confirmarExclusao"></ConfirmDeleteDialog>
    </v-dialog>
</template>

<script>
import apiController from '@/utils/apiController';
import EventBus from '@/utils/eventBus';
import DateUtils from '@/utils/dateutils';
import ConfirmDeleteDialog from '@/components/Dialogs/ConfirmDeleteDialog.vue';

/**
 * Vigências em que as viagens do veículo preferem o hodômetro virtual no km aferido
 * (fim vazio = indefinido). Grava direto pela API, sem passar pelo save do veículo; cada
 * alteração recalcula o km das viagens do período em meses abertos.
 */
export default {
    name: 'VigenciasHodometroDialog',
    components: { ConfirmDeleteDialog },
    props: {
        value: { type: Boolean, default: false },
        veiculo: { type: Object, default: null },
        canEdit: { type: Boolean, default: false },
        canDelete: { type: Boolean, default: false },
    },
    data: () => ({
        vigencias: [],
        loading: false,
        salvando: false,
        formValido: true,
        idEditando: null,
        dtInicio: null,
        dtFim: null,
        observacao: '',
        confirmaExclusao: false,
        vigenciaExcluir: null,
        timeProps: { useSeconds: true },
        corStatus: { vigente: 'green', futura: 'blue', expirada: 'grey' },
    }),
    computed: {
        editando() {
            return this.idEditando != null;
        },
        headers() {
            return [
                { text: this.$t('crudVeiculos.odometerValidity.start'), value: 'dt_inicio_vigencia' },
                { text: this.$t('crudVeiculos.odometerValidity.end'), value: 'dt_fim_vigencia' },
                { text: this.$t('crudVeiculos.odometerValidity.statusLabel'), value: 'status_vigencia' },
                { text: this.$t('crudVeiculos.odometerValidity.notes'), value: 'observacao', sortable: false },
                { text: this.$t('crudVeiculos.odometerValidity.createdBy'), value: 'nome_usuario_inclusao' },
                { text: '', value: 'actions', sortable: false, align: 'end' },
            ];
        },
    },
    watch: {
        value(aberto) {
            if (aberto) {
                this.limpaForm();
                this.carregar();
            }
        },
    },
    methods: {
        async carregar() {
            if (!this.veiculo) return;
            this.loading = true;
            try {
                const response = await apiController.request(apiController.endpoints.getVigenciashodometroveiculo, {
                    params: { idveiculo: this.veiculo.id_veiculo },
                    silent: true,
                });
                this.vigencias = Array.isArray(response.data) ? response.data : [];
            } catch (error) {
                this.vigencias = [];
                EventBus.$emit('show-error', { error });
            } finally {
                this.loading = false;
            }
        },
        limpaForm() {
            this.idEditando = null;
            this.dtInicio = null;
            this.dtFim = null;
            this.observacao = '';
        },
        editar(item) {
            this.idEditando = item.id_vigencia_hodometro;
            this.dtInicio = new Date(item.dt_inicio_vigencia.replace(' ', 'T'));
            this.dtFim = item.dt_fim_vigencia ? new Date(item.dt_fim_vigencia.replace(' ', 'T')) : null;
            this.observacao = item.observacao || '';
        },
        async salvar() {
            const payload = {
                id_veiculo: this.veiculo.id_veiculo,
                dt_inicio_vigencia: DateUtils.getDateSQLFormatted(this.dtInicio),
                dt_fim_vigencia: this.dtFim ? DateUtils.getDateSQLFormatted(this.dtFim) : null,
                observacao: this.observacao || null,
            };
            this.salvando = true;
            try {
                // Chamadas diretas: o manifesto de permissões só enxerga apiController.request(endpoints.X).
                let response;
                if (this.editando) {
                    payload.id_vigencia_hodometro = this.idEditando;
                    response = await apiController.request(apiController.endpoints.updateVigenciahodometroveiculo, { data: payload, silent: true });
                } else {
                    response = await apiController.request(apiController.endpoints.insertVigenciahodometroveiculo, { data: payload, silent: true });
                }
                this.mostraResultado(response.data);
                this.limpaForm();
                await this.carregar();
            } catch (error) {
                // 400 (período inválido/sobreposto) vem com a mensagem traduzida da API.
                this.mostraErro(error);
            } finally {
                this.salvando = false;
            }
        },
        excluir(item) {
            this.vigenciaExcluir = item;
            this.confirmaExclusao = true;
        },
        async confirmarExclusao() {
            const item = this.vigenciaExcluir;
            if (!item) return;
            try {
                const response = await apiController.request(apiController.endpoints.deleteVigenciahodometroveiculo, {
                    data: { id_vigencia_hodometro: item.id_vigencia_hodometro, id_veiculo: this.veiculo.id_veiculo },
                    silent: true,
                });
                this.mostraResultado(response.data);
                if (this.idEditando === item.id_vigencia_hodometro) this.limpaForm();
                await this.carregar();
            } catch (error) {
                this.mostraErro(error);
            }
        },
        mostraResultado(data) {
            EventBus.$emit('show-alert', {
                mensagem: data && data.mensagem,
                tipo: data && data.erro ? 'warning' : 'success',
            });
        },
        mostraErro(error) {
            const mensagem = error && error.response && error.response.data && error.response.data.mensagem;
            if (mensagem) {
                EventBus.$emit('show-alert', { mensagem, tipo: 'error' });
            } else {
                EventBus.$emit('show-error', { error });
            }
        },
    },
};
</script>
